import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2, KeyRound } from "lucide-react";

export default function Login() {
  const { user, ready, login, completeTwoFA } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState("credentials"); // credentials | verify | setup
  const [twofaToken, setTwofaToken] = useState("");
  const [code, setCode] = useState("");
  const [setupData, setSetupData] = useState(null);

  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (badge) badge.style.display = "none";
    return () => { if (badge) badge.style.display = ""; };
  }, []);

  if (ready && user) return <Navigate to={user.role === "customer" ? "/portal" : "/dashboard"} replace />;

  const goHome = (role) => { toast.success("Signed in"); nav(role === "customer" ? "/portal" : "/dashboard"); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    if (res.twofa === "verify") { setTwofaToken(res.token); setStep("verify"); setLoading(false); return; }
    if (res.twofa === "setup") {
      setTwofaToken(res.token);
      try {
        const { data } = await api.post("/auth/2fa/setup", { twofa_token: res.token });
        setSetupData(data); setStep("setup");
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Setup failed"); }
      setLoading(false); return;
    }
    setLoading(false);
    if (res.ok) goHome(res.role); else toast.error(res.error || "Login failed");
  };

  const verify = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify-login", { twofa_token: twofaToken, code });
      const r = completeTwoFA(data); goHome(r.role);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code"); }
    setLoading(false);
  };

  const enable = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { twofa_token: twofaToken, code });
      const r = completeTwoFA(data); goHome(r.role);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="flex items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cert-hub-31/artifacts/shlxk4d2_image-1.png" alt="Doc Q" className="w-10 h-10 object-contain" />
            <span className="font-display text-xl font-bold tracking-tight">Doc Q</span>
          </div>

          {step === "credentials" && (
            <>
              <h1 className="font-display text-4xl font-bold tracking-tight mb-2">Sign in</h1>
              <p className="text-sm text-gray-600 mb-8">Access your inspection &amp; certification workspace.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-gray-700">Email</Label>
                  <Input id="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-full border-gray-300 focus:border-[#01ADEF] focus:ring-1 focus:ring-[#01ADEF]" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold text-gray-700">Password</Label>
                  <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 rounded-full border-gray-300 focus:border-[#01ADEF] focus:ring-1 focus:ring-[#01ADEF]" />
                </div>
                <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full rounded-full bg-[#01ADEF] hover:bg-[#0198D6] text-white font-medium h-11">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            </>
          )}

          {step === "verify" && (
            <>
              <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center mb-4"><KeyRound className="w-5 h-5 text-[#0369A1]" /></div>
              <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Two-factor code</h1>
              <p className="text-sm text-gray-600 mb-6">Enter the 6-digit code from your authenticator app.</p>
              <form onSubmit={verify} className="space-y-4">
                <Input data-testid="twofa-code-input" inputMode="numeric" autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="rounded-full text-center tracking-[0.4em] font-mono text-lg" maxLength={6} />
                <Button type="submit" data-testid="twofa-verify-button" disabled={loading || code.length < 6} className="w-full rounded-full bg-[#01ADEF] hover:bg-[#0198D6] h-11">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & sign in"}</Button>
                <button type="button" onClick={() => { setStep("credentials"); setCode(""); }} className="w-full text-xs text-gray-500 hover:text-gray-800">Back to sign in</button>
              </form>
            </>
          )}

          {step === "setup" && setupData && (
            <>
              <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Set up two-factor</h1>
              <p className="text-sm text-gray-600 mb-4">Your role requires 2FA. Scan the QR code with Google Authenticator or Authy, then enter the 6-digit code.</p>
              <div className="flex justify-center mb-4">
                <img src={setupData.qr_png} alt="2FA QR" data-testid="twofa-qr" className="w-44 h-44 border border-gray-200 rounded-md" />
              </div>
              <p className="text-[11px] text-gray-500 mb-4 text-center">Can't scan? Enter this key manually:<br /><span className="font-mono font-bold text-gray-700 break-all">{setupData.secret}</span></p>
              <form onSubmit={enable} className="space-y-4">
                <Input data-testid="twofa-setup-code-input" inputMode="numeric" autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="rounded-full text-center tracking-[0.4em] font-mono text-lg" maxLength={6} />
                <Button type="submit" data-testid="twofa-enable-button" disabled={loading || code.length < 6} className="w-full rounded-full bg-[#01ADEF] hover:bg-[#0198D6] h-11">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate & sign in"}</Button>
                <button type="button" onClick={() => { setStep("credentials"); setCode(""); }} className="w-full text-xs text-gray-500 hover:text-gray-800">Back to sign in</button>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="hidden md:block relative">
        <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cert-hub-31/artifacts/kzih77ph_image-1%20%283%29.jpeg" alt="Doc Q" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B1B]/85 via-[#1B1B1B]/20 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="border-l-4 border-[#FF5A00] pl-5">
            <div className="text-xs uppercase tracking-[0.3em] mb-3 text-white/60">Certification &amp; Compliance Suite</div>
            <h2 className="font-display text-4xl font-bold leading-tight">Inspect, certify, and stay compliant — Cookes 360.</h2>
          </div>
        </div>
      </div>
    </div>
  );
}
